import React, { useEffect, useState, useCallback } from "react";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkle, Plus, ArrowClockwise, MagnifyingGlass, XCircle, GitMerge } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { DuplicateDetector } from "@/components/DuplicateDetector";
import { OriginBadge } from "@/components/OriginBadge";

function intentBadge(intent) {
  const cls = intent === "high" ? "bg-emerald-500 text-white"
    : intent === "medium" ? "bg-amber-500 text-white"
    : "bg-slate-500 text-white";
  return <Badge className={cls}>{intent}</Badge>;
}

export default function Leads() {
  const { t, lang } = useLang();
  const { user: me } = useAuth();
  const canMerge = me && (me.role === "admin" || me.role === "supervisor");
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [agentFilter, setAgentFilter] = useState("all");
  const [brandFilter, setBrandFilter] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [originFilter, setOriginFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // Sprint 35B — Contact merge dialog state
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeQ, setMergeQ] = useState("");
  const [mergeTarget, setMergeTarget] = useState(null); // the primary we keep
  const [mergeBusy, setMergeBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", brand: "Toyota", model: "", budget: "",
    purchase_timeline: "1-3m", financing_interest: false, comments: "",
  });

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filter && filter !== "all") params.set("intent", filter);
    if (searchQ.trim()) params.set("q", searchQ.trim());
    if (agentFilter !== "all") params.set("assigned_to", agentFilter);
    if (brandFilter !== "all") params.set("brand", brandFilter);
    if (originFilter !== "all") params.set("first_channel", originFilter);
    if (dateRange !== "all") {
      const now = new Date(); const from = new Date(now);
      if (dateRange === "today") from.setHours(0, 0, 0, 0);
      else if (dateRange === "7d") from.setDate(now.getDate() - 7);
      else if (dateRange === "30d") from.setDate(now.getDate() - 30);
      params.set("date_from", from.toISOString());
    }
    const qs = params.toString();
    const [{ data: leadsData }, { data: usersData }] = await Promise.all([
      api.get(`/leads${qs ? `?${qs}` : ""}`),
      api.get("/users"),
    ]);
    setLeads(leadsData);
    setUsers(usersData);
  }, [filter, searchQ, agentFilter, brandFilter, dateRange, originFilter]);

  const userById = (id) => users.find((u) => u.id === id);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...form, budget: form.budget ? parseFloat(form.budget) : null };
      const { data } = await api.post("/leads", payload);
      toast.success(`Lead creado con score ${data.score}`);
      setOpen(false);
      setForm({ name: "", phone: "", email: "", brand: "Toyota", model: "", budget: "", purchase_timeline: "1-3m", financing_interest: false, comments: "" });
      load();
    } catch (err) {
      toast.error("Error creando lead");
    } finally {
      setCreating(false);
    }
  };

  const rescore = async (id) => {
    toast.info(lang === "es" ? "Recalculando…" : "Rescoring…");
    try {
      const { data } = await api.post(`/leads/${id}/rescore`);
      setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
      if (selected?.id === id) setSelected(data);
      toast.success(`Score: ${data.score}`);
    } catch {
      toast.error("Error");
    }
  };

  // Sprint 35B — Candidates for merge: all other leads with a customer_id,
  // optionally filtered by the dialog's search box. We dedupe by customer_id
  // because many leads can share one contact.
  const mergeCandidates = React.useMemo(() => {
    if (!selected) return [];
    const q = mergeQ.trim().toLowerCase();
    const seen = new Set([selected.customer_id]);
    const out = [];
    for (const l of leads) {
      if (!l.customer_id || seen.has(l.customer_id)) continue;
      const hay = [l.name, l.email, l.phone, l.brand, l.model].join(" ").toLowerCase();
      if (q && !hay.includes(q)) continue;
      seen.add(l.customer_id);
      out.push(l);
      if (out.length >= 20) break;
    }
    return out;
  }, [leads, selected, mergeQ]);

  const doMerge = async () => {
    if (!selected || !mergeTarget) return;
    if (!window.confirm(
      lang === "es"
        ? `Vas a fusionar "${selected.name}" dentro de "${mergeTarget.name}". Esta acción es irreversible. ¿Continuar?`
        : `This will merge "${selected.name}" into "${mergeTarget.name}". This is irreversible. Continue?`
    )) return;
    setMergeBusy(true);
    try {
      const { data } = await api.post("/customers/merge", {
        primary_id: mergeTarget.customer_id,
        source_id: selected.customer_id,
      });
      toast.success(
        lang === "es"
          ? `Fusión completa · ${data.conversations_reassigned} conv · ${data.messages_reassigned} msgs · ${data.leads_reassigned} contactos`
          : `Merge done · ${data.conversations_reassigned} conv · ${data.messages_reassigned} msgs`
      );
      setMergeOpen(false);
      setMergeTarget(null);
      setMergeQ("");
      setSelected(null);
      await load();
    } catch (err) {
      toast.error(
        lang === "es"
          ? `Error en fusión: ${err?.response?.data?.detail || err.message}`
          : `Merge failed: ${err?.response?.data?.detail || err.message}`
      );
    } finally {
      setMergeBusy(false);
    }
  };

  return (
    <>
      <Topbar
        title={t("leads.title")}
        subtitle={t("leads.subtitle")}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="gap-1.5 ml-2" data-testid="btn-new-lead">
                <Plus size={14}/> {t("leads.new")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t("leads.new")}</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3" data-testid="form-new-lead">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("common.name")}</Label>
                    <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="lead-name"/>
                  </div>
                  <div>
                    <Label>{t("common.phone")}</Label>
                    <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="lead-phone"/>
                  </div>
                  <div className="col-span-2">
                    <Label>{t("common.email")}</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="lead-email"/>
                  </div>
                  <div>
                    <Label>Brand</Label>
                    <Input value={form.brand} onChange={(e) => setForm({...form, brand: e.target.value})} data-testid="lead-brand"/>
                  </div>
                  <div>
                    <Label>Model</Label>
                    <Input value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} data-testid="lead-model"/>
                  </div>
                  <div>
                    <Label>Budget (RD$)</Label>
                    <Input type="number" value={form.budget} onChange={(e) => setForm({...form, budget: e.target.value})} data-testid="lead-budget"/>
                  </div>
                  <div>
                    <Label>{t("leads.timeline")}</Label>
                    <Select value={form.purchase_timeline} onValueChange={(v) => setForm({...form, purchase_timeline: v})}>
                      <SelectTrigger data-testid="lead-timeline"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediate">Immediate</SelectItem>
                        <SelectItem value="1-3m">1-3 months</SelectItem>
                        <SelectItem value="3-6m">3-6 months</SelectItem>
                        <SelectItem value="6m+">6+ months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>{t("common.notes")}</Label>
                    <Textarea value={form.comments} onChange={(e) => setForm({...form, comments: e.target.value})} data-testid="lead-comments"/>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={creating} className="gap-1.5" data-testid="lead-submit">
                    <Sparkle size={14}/> {creating ? t("common.loading") : t("common.create")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-8 space-y-4 animate-fade-in flex-1 overflow-y-auto min-h-0">
        {canMerge && <DuplicateDetector onMerged={load} />}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
          <div className="relative md:col-span-2">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder={lang === "es" ? "Buscar por nombre, teléfono, email, marca o modelo…" : "Search by name, phone, email, brand or model…"}
              className="pl-8 h-9"
              data-testid="leads-search"
            />
            {searchQ && (
              <button onClick={() => setSearchQ("")} className="absolute right-2 top-2 text-muted-foreground hover:text-foreground" data-testid="leads-search-clear">
                <XCircle size={14} weight="fill" />
              </button>
            )}
          </div>
          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="leads-agent-filter"
          >
            <option value="all">{lang === "es" ? "Todos los agentes" : "All agents"}</option>
            {users.filter((u) => ["sales", "bdc", "supervisor"].includes(u.role)).map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="leads-date-filter"
          >
            <option value="all">{lang === "es" ? "Cualquier fecha" : "Any date"}</option>
            <option value="today">{lang === "es" ? "Hoy" : "Today"}</option>
            <option value="7d">{lang === "es" ? "Últimos 7 días" : "Last 7 days"}</option>
            <option value="30d">{lang === "es" ? "Últimos 30 días" : "Last 30 days"}</option>
          </select>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList>
              <TabsTrigger value="all" data-testid="lead-filter-all">{t("common.all")}</TabsTrigger>
              <TabsTrigger value="high" data-testid="lead-filter-high">{t("leads.high")}</TabsTrigger>
              <TabsTrigger value="medium" data-testid="lead-filter-medium">{t("leads.medium")}</TabsTrigger>
              <TabsTrigger value="low" data-testid="lead-filter-low">{t("leads.low")}</TabsTrigger>
            </TabsList>
          </Tabs>
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="leads-brand-filter"
          >
            <option value="all">{lang === "es" ? "Todas las marcas" : "All brands"}</option>
            {["Toyota", "Suzuki", "Cadillac", "GMC", "Chevrolet", "Isuzu"].map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          <select
            value={originFilter}
            onChange={(e) => setOriginFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            data-testid="leads-origin-filter"
          >
            <option value="all">{lang === "es" ? "Canal de origen: todos" : "Origin: all"}</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="messenger">Messenger</option>
            <option value="instagram">Instagram</option>
            <option value="web">Web</option>
          </select>
          {leads.length > 0 && (
            <span className="text-xs text-muted-foreground ml-auto" data-testid="leads-count">
              {leads.length} {lang === "es" ? "contactos" : "contacts"}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 border border-border rounded-md bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-4 py-3">{t("common.name")}</th>
                  <th className="text-left px-4 py-3">{t("leads.brand")}</th>
                  <th className="text-left px-4 py-3">{t("leads.score")}</th>
                  <th className="text-left px-4 py-3">{t("leads.intent")}</th>
                  <th className="text-left px-4 py-3">{t("leads.assigned")}</th>
                  <th className="text-left px-4 py-3">{t("common.status")}</th>
                  <th className="text-right px-4 py-3">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">{t("common.empty")}</td></tr>
                )}
                {leads.map((l) => {
                  const agent = userById(l.assigned_to);
                  return (
                  <tr
                    key={l.id}
                    className={`border-b border-border cursor-pointer hover:bg-secondary transition-colors ${selected?.id === l.id ? "bg-accent" : ""}`}
                    onClick={() => setSelected(l)}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium flex items-center gap-1.5">{l.name} <OriginBadge channel={l.first_channel} testid={`lead-origin-${l.id}`} /></div>
                      <div className="text-xs text-muted-foreground">{l.phone || l.email}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{l.brand} {l.model}</td>
                    <td className="px-4 py-3 font-heading text-lg font-bold">{l.score}</td>
                    <td className="px-4 py-3">{intentBadge(l.intent)}</td>
                    <td className="px-4 py-3 text-xs">
                      {agent ? (
                        <div className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[10px] font-bold">
                            {agent.name[0]}
                          </div>
                          <span>{agent.name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground italic">
                          {lang === "es" ? "Nurturing" : "Nurturing"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3"><Badge variant="outline" className="uppercase text-[10px]">{l.status}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); rescore(l.id); }}
                        data-testid={`btn-rescore-${l.id}`}
                        className="gap-1"
                      >
                        <ArrowClockwise size={12}/> {t("leads.rescore")}
                      </Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Selected lead detail */}
          <div className="border border-border rounded-md p-6 bg-card sticky top-24 h-fit" data-testid="lead-detail">
            {!selected ? (
              <div className="text-sm text-muted-foreground text-center py-12">
                {lang === "es" ? "Selecciona un lead para ver detalles" : "Select a lead to view details"}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="overline mb-1">{t("common.name")}</div>
                    <div className="font-heading text-xl font-bold truncate">{selected.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{selected.email} · {selected.phone}</div>
                    {selected.first_channel && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        Primer contacto: <OriginBadge channel={selected.first_channel} testid="lead-detail-origin" />
                      </div>
                    )}
                  </div>
                  {canMerge && selected.customer_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5 border-amber-500/40 text-amber-600 hover:bg-amber-500/10 shrink-0"
                      onClick={() => { setMergeOpen(true); setMergeTarget(null); setMergeQ(""); }}
                      data-testid="btn-merge-contact"
                      title={lang === "es" ? "Fusionar este contacto con otro" : "Merge this contact with another"}
                    >
                      <GitMerge size={14} weight="bold" />
                      {lang === "es" ? "Fusionar" : "Merge"}
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-4 p-4 rounded-md bg-secondary">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("leads.score")}</div>
                    <div className="font-heading text-4xl font-bold">{selected.score}</div>
                  </div>
                  <div>{intentBadge(selected.intent)}</div>
                </div>
                {selected.assigned_to && (
                  <div>
                    <div className="overline mb-1">{t("leads.assigned")}</div>
                    {(() => {
                      const a = userById(selected.assigned_to);
                      return a ? (
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">{a.name[0]}</div>
                          <div>
                            <div className="text-sm font-medium">{a.name}</div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{a.role} · {a.branch || "—"}</div>
                          </div>
                        </div>
                      ) : <div className="text-xs text-muted-foreground">—</div>;
                    })()}
                    {selected.first_response_sla_seconds && (
                      <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                        SLA: {selected.first_response_sla_seconds < 120 ? `${selected.first_response_sla_seconds}s` : `${Math.round(selected.first_response_sla_seconds / 60)}min`}
                        {selected.first_response_at && (
                          <span className="ml-2 text-emerald-500">✓ {lang === "es" ? "respondido" : "responded"}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <div className="overline mb-1">{t("leads.brand")}</div>
                  <div className="text-sm">{selected.brand} {selected.model}</div>
                </div>
                <div>
                  <div className="overline mb-1">{t("leads.signals")}</div>
                  <ul className="text-xs space-y-1 list-disc pl-4">
                    {(selected.buying_signals || []).map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
                {selected.recommended_action && (
                  <div>
                    <div className="overline mb-1">{t("leads.action")}</div>
                    <div className="text-xs p-3 rounded-md bg-primary/10 border border-primary/20">
                      💡 {selected.recommended_action}
                    </div>
                  </div>
                )}
                {selected.summary && (
                  <div>
                    <div className="overline mb-1">{t("inbox.summary")}</div>
                    <div className="text-xs text-muted-foreground">{selected.summary}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sprint 35B — Merge Contact Dialog */}
      <Dialog open={mergeOpen} onOpenChange={(v) => { if (!mergeBusy) setMergeOpen(v); }}>
        <DialogContent className="max-w-xl" data-testid="merge-contact-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge size={18} weight="bold" />
              {lang === "es" ? "Fusionar contacto" : "Merge contact"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {lang === "es"
                ? "Mueve todas las conversaciones y leads de este contacto a otro, y elimina el duplicado."
                : "Move all conversations and leads from this contact to another, and delete the duplicate."}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="p-3 rounded-md bg-rose-500/10 border border-rose-500/30 text-xs">
                <div className="font-medium mb-1">
                  {lang === "es"
                    ? "Vas a fusionar este contacto dentro de otro."
                    : "You are merging this contact into another."}
                </div>
                <div className="text-muted-foreground">
                  {lang === "es"
                    ? "Todas las conversaciones, mensajes, leads y solicitudes se moverán al contacto destino. El contacto actual será eliminado. Esta acción es irreversible."
                    : "All conversations, messages, leads and requests will move to the target. This contact will be deleted. Irreversible."}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 rounded-md border border-border bg-secondary/40">
                  <div className="overline mb-1 text-rose-600">
                    {lang === "es" ? "Fuente (se elimina)" : "Source (removed)"}
                  </div>
                  <div className="font-medium truncate">{selected.name}</div>
                  <div className="text-muted-foreground truncate">{selected.email || "—"} · {selected.phone || "—"}</div>
                </div>
                <div className="p-3 rounded-md border border-emerald-500/40 bg-emerald-500/5">
                  <div className="overline mb-1 text-emerald-600">
                    {lang === "es" ? "Destino (se queda)" : "Target (kept)"}
                  </div>
                  {mergeTarget ? (
                    <>
                      <div className="font-medium truncate">{mergeTarget.name}</div>
                      <div className="text-muted-foreground truncate">{mergeTarget.email || "—"} · {mergeTarget.phone || "—"}</div>
                    </>
                  ) : (
                    <div className="text-muted-foreground italic">
                      {lang === "es" ? "Selecciona abajo…" : "Pick below…"}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="merge-search" className="text-xs">
                  {lang === "es" ? "Buscar contacto destino" : "Search target contact"}
                </Label>
                <Input
                  id="merge-search"
                  value={mergeQ}
                  onChange={(e) => setMergeQ(e.target.value)}
                  placeholder={lang === "es" ? "Nombre, email o teléfono…" : "Name, email or phone…"}
                  data-testid="merge-search-input"
                  className="mt-1"
                />
              </div>

              <div className="max-h-56 overflow-auto border border-border rounded-md divide-y divide-border">
                {mergeCandidates.length === 0 ? (
                  <div className="p-3 text-xs text-muted-foreground text-center">
                    {lang === "es" ? "Sin candidatos. Refina la búsqueda." : "No candidates."}
                  </div>
                ) : (
                  mergeCandidates.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setMergeTarget(c)}
                      data-testid={`merge-candidate-${c.id}`}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-primary/5 flex items-center justify-between gap-2 ${mergeTarget?.id === c.id ? "bg-emerald-500/10" : ""}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{c.name}</div>
                        <div className="text-muted-foreground truncate">{c.email || "—"} · {c.phone || "—"}</div>
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.brand} {c.model}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)} disabled={mergeBusy}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button
              onClick={doMerge}
              disabled={!mergeTarget || mergeBusy}
              data-testid="btn-merge-confirm"
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {mergeBusy
                ? (lang === "es" ? "Fusionando…" : "Merging…")
                : (lang === "es" ? "Fusionar" : "Merge")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
